import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { usePanelShown } from './panelShown';

// Web a11y: Escape dismisses the open sheet or dialog. Layers can stack
// (UpgradeAccountSheet over AddPropertySheet, a confirm over a sheet), so a
// module-level LIFO stack decides which one responds, and page-level Escape
// handlers (the defense plan editor and viewer close on Escape) check
// isEscapeLayerOpen() so one press never closes a dialog and the panel under
// it. Kept out of AdaptiveSheet so ConfirmDialog can register without
// importing gorhom.
const escapeStack: symbol[] = [];

export function isEscapeLayerOpen(): boolean {
  return escapeStack.length > 0;
}

function useEscapeToken(requested: boolean, onEscape: (() => void) | null) {
  // A layer inside a DockedPanel that is sliding out gives up its Escape the
  // moment the exit starts, so a press during the exit reaches whatever is
  // underneath.
  const panelShown = usePanelShown();
  const active = requested && panelShown;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active || Platform.OS !== 'web') return;
    const token = Symbol('escape-layer');
    escapeStack.push(token);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape during IME composition cancels the composition; it is not a
      // request to close anything.
      if (e.isComposing) return;
      if (escapeStack[escapeStack.length - 1] !== token) return;
      if (!onEscapeRef.current) return;
      // This layer owns the press, so nothing under it may act on the same
      // event. Without this a page-level bubble-phase handler (the plan
      // editor's) still saw the press: React flushes this layer's unmount
      // between the capture and bubble phases (a sync-lane update from a
      // native listener commits at the microtask checkpoint after this
      // callback returns), so by the bubble phase the token was already off
      // the stack and isEscapeLayerOpen() answered false. Found Sept 23
      // 2026: one Escape closed the Layers panel AND the plan editor under it.
      e.preventDefault();
      e.stopPropagation();
      onEscapeRef.current();
    };
    // Capture phase, and it has to be. react-native-web's TextInput calls
    // stopPropagation() on EVERY keydown ("Prevent key events bubbling",
    // its #612), so a bubble-phase listener on document never sees Escape
    // while the caret is in a field — which is most of the time a sheet
    // with a form is open. Found Sept 23 2026 by the audience invite
    // sheet's first audit run.
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      const i = escapeStack.indexOf(token);
      if (i >= 0) escapeStack.splice(i, 1);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active]);
}

/** Escape closes this layer when it's the topmost one. */
export function useEscapeToClose(active: boolean, close: () => void) {
  useEscapeToken(active, close);
}

/** Claims an Escape layer without handling the key, for a surface that
 *  handles Escape itself (RN Modal's onRequestClose), so page-level
 *  handlers still stand aside while it's open. */
export function useEscapeLayer(active: boolean) {
  useEscapeToken(active, null);
}
